import { type Static, Type } from "@sinclair/typebox";
import { ApiError, HttpStatusCode, Problems } from "@zuplo/errors";
import { Hono } from "hono";
import { editorConfig } from "../constants.js";
import type { CommonLogger } from "../interfaces.js";
import {
  type FileSystemPort,
  PathNotFoundError,
  UnsafePathError,
} from "../ports.js";
import { normalizeDisplayPath } from "../utils/path.js";
import { validate } from "../validation.js";

/**
 * Reads a file, treating a missing one as absent rather than as an error.
 *
 * Several callers here only care whether there is content to compare against,
 * and a file that isn't there can't conflict with anything.
 */
async function readIfExists(fs: FileSystemPort, path: string) {
  try {
    return await fs.readFileWithStat(path);
  } catch (err) {
    if (err instanceof PathNotFoundError) {
      return undefined;
    }
    throw err;
  }
}

/**
 * Decides whether writing `newContent` would clobber an edit made since the
 * client loaded the file.
 *
 * HTTP date headers only have second precision, so timestamps are compared
 * truncated to seconds, and a timestamp mismatch on its own isn't a conflict as
 * long as what's stored matches what's being saved.
 */
async function hasSaveConflict(
  fs: FileSystemPort,
  path: string,
  ifUnmodifiedSince: string,
  newContent: string,
): Promise<boolean> {
  const file = await readIfExists(fs, path);
  if (!file) {
    return false;
  }

  const clientTimestamp = Math.floor(
    new Date(ifUnmodifiedSince).getTime() / 1000,
  );
  const fileTimestamp = Math.floor(file.mtime.getTime() / 1000);

  if (fileTimestamp === clientTimestamp) {
    return false;
  }

  return file.content !== newContent;
}

interface FsRoutesOptions {
  fs: FileSystemPort;
  logger: CommonLogger;
}

const ReadDirQuerystring = Type.Object({
  path: Type.Optional(Type.String()),
  recursive: Type.Optional(Type.String()),
});

const ReadDirResponse = Type.Object({
  result: Type.Array(Type.Tuple([Type.String(), Type.Number()])),
  path: Type.String(),
});

const ReadFileQuerystring = Type.Object({
  path: Type.String(),
});

const WriteFileQuerystring = Type.Object({
  path: Type.String(),
});

const RenameFileQuerystring = Type.Object({
  path: Type.String(),
});

const RenameFileBody = Type.Object({
  newPath: Type.String(),
});

const DeleteFileQuerystring = Type.Object({
  path: Type.String(),
});

const CreateDirQuerystring = Type.Object({
  path: Type.String(),
});

/** Tells the portal not to cache file reads — the stored file is the truth. */
const noCacheHeaders = {
  "Cache-Control": "no-store, no-cache, must-revalidate",
  Pragma: "no-cache",
};

/** The entry-type codes the portal's file tree expects. */
const FILE = 1;
const DIRECTORY = 2;

/**
 * The top-level project directories the file tree shows, in display order.
 *
 * Listed explicitly rather than discovered so the tree's order is stable and
 * unrelated top-level files (node_modules, .git, lockfiles) stay out of it.
 */
const projectDirs = ["config", "modules", "schemas", "tests", "docs"] as const;

/**
 * Turns a path the client sent into an `ApiError` when it escapes the project.
 *
 * The port refuses traversal on the way through, and a request that attempts it
 * is a client error, not a server fault — without this it would surface as a 500.
 */
function toApiError(err: unknown): unknown {
  if (err instanceof UnsafePathError) {
    return new ApiError({
      ...Problems.BAD_REQUEST,
      detail: `Invalid path: ${err.path}`,
    });
  }
  return err;
}

export function createFsRoutes(options: FsRoutesOptions): Hono {
  const { fs, logger } = options;
  const app = new Hono();

  // A path that climbs out of the project can arrive at any of these routes, so
  // it's translated once here rather than guarded in each handler.
  app.use(async (_c, next) => {
    try {
      await next();
    } catch (err) {
      throw toApiError(err);
    }
  });

  // Match portal's file API shape but simplified for local: /api/fs/dir
  app.get("/dir", validate("query", ReadDirQuerystring), async (c) => {
    const query = c.req.valid("query");
    const requestPath = query.path ?? "/";
    const recursive = query.recursive === "true";
    const fileTree: Array<[string, number]> = [];

    const readDirRecursive = async (dirPath: string) => {
      let entries: Awaited<ReturnType<FileSystemPort["listDir"]>>;
      try {
        entries = await fs.listDir(dirPath);
      } catch (err) {
        // A directory that can't be read is skipped rather than failing the
        // whole tree, but logged so it's diagnosable.
        logger.warn({ err, dirPath }, "Could not read directory, skipping");
        return;
      }

      for (const entry of entries) {
        const entryPath = dirPath ? `${dirPath}/${entry.name}` : entry.name;
        // Always prepend / to match portal's path format
        const displayPath = normalizeDisplayPath(entryPath);

        if (entry.type === "directory") {
          fileTree.push([displayPath, DIRECTORY]);
          if (recursive) {
            await readDirRecursive(entryPath);
          }
        } else {
          fileTree.push([displayPath, FILE]);
        }
      }
    };

    if (requestPath === "/") {
      // Add each parent dir before its contents, so the client can build the
      // tree in one pass.
      for (const dir of projectDirs) {
        if ((await fs.statPath(dir))?.type === "directory") {
          fileTree.push([normalizeDisplayPath(dir), DIRECTORY]);
          await readDirRecursive(dir);
        }
      }
    } else if ((await fs.statPath(requestPath))?.type === "directory") {
      await readDirRecursive(requestPath);
    }

    // Return in backend's format: { path: string, result: FileList }
    const body: Static<typeof ReadDirResponse> = {
      path: requestPath,
      result: fileTree,
    };
    return c.json(body, 200, noCacheHeaders);
  });

  // Match portal's file API shape but simplified for local: /api/fs/file
  app.get("/file", validate("query", ReadFileQuerystring), async (c) => {
    const filePath = c.req.valid("query").path;

    const file = await readIfExists(fs, filePath);
    if (!file) {
      throw new ApiError({
        ...Problems.NOT_FOUND,
        detail: `Cannot find file: ${filePath}`,
      });
    }

    // The mtime describes the content it arrived with, so the Last-Modified the
    // client saves can't refer to a different version than what it got.
    return c.text(file.content, 200, {
      "Last-Modified": file.mtime.toUTCString(),
      ...noCacheHeaders,
    });
  });

  // Write/create a file: POST /file?path=...
  app.post("/file", validate("query", WriteFileQuerystring), async (c) => {
    const filePath = c.req.valid("query").path;

    // The body is stored exactly as it arrived, whatever the content type
    // claims — the editor sends file contents, not a parsable payload.
    const content = await c.req.text();

    // Check if file exists and has been modified since client loaded it
    // Skip conflict check if x-force-save header is present (user resolved conflict)
    const forceSave = c.req.header("x-force-save");
    const ifUnmodifiedSince = c.req.header("if-unmodified-since");

    if (
      !forceSave &&
      ifUnmodifiedSince &&
      (await hasSaveConflict(fs, filePath, ifUnmodifiedSince, content))
    ) {
      // Precondition failed - file was modified since client loaded it
      throw new ApiError({
        status: 412,
        type: "https://zup.fail/http-status/412",
        title: "Precondition Failed",
        detail: "The file has been modified on disk since you loaded it.",
        instance: filePath,
      });
    }

    const stat = await fs.writeFile(filePath, content);

    // Return the new timestamp in Last-Modified header so client can update its value
    return c.json({ buildId: `local-build-${Date.now()}` }, 200, {
      "Last-Modified": stat.mtime.toUTCString(),
    });
  });

  // Write/create multiple files: POST /files (multipart)
  app.post("/files", async (c) => {
    if (!c.req.header("content-type")?.includes("multipart/form-data")) {
      throw new ApiError({
        ...Problems.BAD_REQUEST,
        detail: "Request must be multipart/form-data",
      });
    }

    // The whole request is buffered before any file is written. The body limit
    // keeps that bounded.
    let formData: FormData;
    try {
      formData = await c.req.formData();
    } catch (err) {
      throw new ApiError(
        {
          ...Problems.BAD_REQUEST,
          detail: "Could not parse the multipart/form-data request body",
        },
        { cause: err },
      );
    }

    const conflictedFiles: string[] = [];
    const savedFiles: Record<string, number> = {}; // Track saved files and their new timestamps

    for (const part of formData.values()) {
      if (!(part instanceof File) || !part.name) {
        continue;
      }

      const displayPath = normalizeDisplayPath(part.name);

      // Written as bytes rather than as a decoded string, so an upload that
      // isn't valid UTF-8 is stored as sent instead of being mangled by a
      // decode on the way in. Reading it back is a separate matter — the file
      // API is text-only, so this doesn't make binary files round-trip. The
      // decoded form here is only for comparing against what the client holds.
      const bytes = new Uint8Array(await part.arrayBuffer());
      if (bytes.byteLength > editorConfig.file.maxFileSize) {
        throw new ApiError({
          type: "https://zup.fail/http-status/413",
          title: "Payload Too Large",
          status: HttpStatusCode.PAYLOAD_TOO_LARGE,
          detail: `File exceeds the maximum size of ${editorConfig.file.maxFileSize} bytes`,
          instance: displayPath,
        });
      }

      const newContent = new TextDecoder().decode(bytes);

      // Check for conflicts using timestamp and content comparison. Each file
      // carries its own If-Unmodified-Since, keyed by path.
      const ifUnmodifiedSince = c.req.header(
        `x-if-unmodified-since-${encodeURIComponent(displayPath)}`,
      );

      if (
        ifUnmodifiedSince &&
        (await hasSaveConflict(fs, part.name, ifUnmodifiedSince, newContent))
      ) {
        // Precondition failed - file was modified since client loaded it
        conflictedFiles.push(displayPath);
        continue;
      }

      const stat = await fs.writeFile(part.name, bytes);
      savedFiles[displayPath] = stat.mtime.getTime();
    }

    if (conflictedFiles.length > 0) {
      throw new ApiError({
        status: 412,
        type: "https://zup.fail/http-status/412",
        title: "Precondition Failed",
        detail: `At least one file has been modified on disk since you loaded them.`,
        instance: JSON.stringify({ conflictedFiles }),
      });
    }

    return c.json({
      buildId: `local-build-${Date.now()}`,
      files: savedFiles,
    });
  });

  // Rename a file: PATCH /api/fs/file?path=...
  app.patch(
    "/file",
    validate("query", RenameFileQuerystring),
    validate("json", RenameFileBody),
    async (c) => {
      const oldPath = c.req.valid("query").path;
      const { newPath } = c.req.valid("json");

      try {
        await fs.rename(oldPath, newPath);
      } catch (err) {
        if (err instanceof PathNotFoundError) {
          throw new ApiError({
            ...Problems.NOT_FOUND,
            detail: `Cannot find file: ${oldPath}`,
          });
        }
        throw err;
      }

      return c.json({
        status: "redeploy successful",
        buildId: `local-build-${Date.now()}`,
      });
    },
  );

  // Delete a file: DELETE /api/fs/delete?path=...
  app.delete("/delete", validate("query", DeleteFileQuerystring), async (c) => {
    const filePath = c.req.valid("query").path;

    try {
      await fs.remove(filePath);
    } catch (err) {
      if (err instanceof PathNotFoundError) {
        throw new ApiError({
          ...Problems.NOT_FOUND,
          detail: `Cannot find file: ${filePath}`,
        });
      }
      throw err;
    }

    return c.json({ buildId: `local-build-${Date.now()}` });
  });

  // Create a directory: POST /api/fs/dir?path=...
  app.post("/dir", validate("query", CreateDirQuerystring), async (c) => {
    await fs.mkdirp(c.req.valid("query").path);
    return c.body(null, 201);
  });

  return app;
}
